import log from "loglevel";
import _ from "lodash";
import { Map } from "immutable";
import contentDisposition from "content-disposition";
import { Dispatch, Middleware, MiddlewareAPI } from "redux";
import { isType, Action, ActionCreator } from "typescript-fsa";

import * as actions from "../actions";
import { API_URL, API_REQUEST_EXPIRATION } from "../constants";
import { ApiHelper } from "../helper";
import { ApiRequest } from "../requests";
import {
    ApiResponse,
    ApiResponseError,
    ApiSales,
    ApiDemographics,
    ApiHeadlineStats,
    ApiModel,
    isApiModel,
    isApiModelCollection,
    isApiModelCollectionPaginated,
    isApiModelTrendsCollectionPaginated,
    isApiId,
    isApiResponseAuth,
    isApiSales,
    isApiDemographics,
    isApiHeadlineStats,
    isApiSearch,
    isApiUrl,
} from "../models";
import { ApiRequestResponse, ApiRequestErrorPair, ApiOptions } from "../models/actions";
import { ApiState } from "../models/state";
import { AuthDetails, AuthState } from "@auth/models";
import { getAccessToken } from "@auth/selectors";
import { LocalisationState, Region } from "@localisation/models";
import { getLanguage } from "@localisation/selectors";
import { PaginationResult, TrendsPaginationResult, HttpStateError, StateError } from "@orm/models";

type State = AuthState & ApiState & LocalisationState;

const api: Middleware = <S>(apiMid: MiddlewareAPI<S>) => (next: Dispatch<S>) =>
    (action: any | actions.ApiAction) => {
        // Only react to api request actions.
        if (isType(action, actions.apiRequest)) {
            // Dispatch the action now.
            const nextS = next as Dispatch<State>;
            nextS(action);

            const state: State = apiMid.getState() as State;
            const options = action.payload.options;
            const endpoint: string = options.endpoint;
            const method: string = options.method;
            const payload: any = options.body;
            const isSecure: boolean = options.secured;

            // Fire the request action
            if (options.types.length > 0) {
                nextS(options.types[0](action.payload));
            }
            log.info("Attempting '" + method + "' API call to " + endpoint);

            const headers = new Headers();
            headers.append("Accept", "application/json");
            const fetchOptions: any = {
                method,
            };

            // The language
            headers.append("Content-Language", getLanguage(state));

            // Setup the payload
            if (payload !== undefined && payload !== null) {
                if (payload instanceof File) {
                    fetchOptions.body = payload;
                    headers.append("Content-Type", payload.type);
                    headers.append("Content-Disposition", contentDisposition(payload.name));
                }
                else {
                    fetchOptions.body = JSON.stringify(payload);
                    headers.append("Content-Type", "application/json");
                }
            }

            // Add the auth header, if required
            if (isSecure) {
                if (state.auth != null && getAccessToken(state) != null) {
                    headers.append("Authorization", "Bearer " + getAccessToken(state));
                }
                else {
                    // If we need to be logged in, we can't call the api.
                    handleApiFailure(nextS, action, null, true);
                    return;
                }
            }
            fetchOptions.headers = headers;

            return doFetch<State>(endpoint, fetchOptions, options, action, nextS);
        }
        else {
            return next(action);
        }
    };

async function doFetch<S extends State>(
    endpoint: string,
    fetchOptions: any,
    options: ApiOptions,
    action: Action<ApiRequest>,
    next: Dispatch<S>,
) {
    let promise: Promise<any> = null;
    // Use XHR for blob transfers to get the progress.
    if (fetchOptions.body instanceof Blob) {
        promise = futch(`${API_URL}${endpoint}`, fetchOptions, (e: ProgressEvent) => {
            next(actions.apiRequestProgress({
                progress: e.loaded / e.total,
                request: action.payload,
            }));
        });
    }
    else {
        promise = fetch(`${API_URL}${endpoint}`, fetchOptions);
    }
    return promise
        .then((response: Response) => {
            // Deal with different types of responses.
            if (response.status < 400) {
                // Handle redirects
                const expectedEndpoint = action.payload.getEndpoint();
                const actualEndpoint = response.url.replace(API_URL, "");
                if (
                    expectedEndpoint !== actualEndpoint
                    && actualEndpoint !== ""
                    && action.payload.options.types.length > 3
                ) {
                    handleApiRedirect(next, action, actualEndpoint);
                }
                else if (options.file != null && options.file) {
                    response.blob()
                        .then((blob: Blob) => {
                            // Get the filename.
                            let filename: string = "filename";
                            if (response.headers.has("Content-Disposition")) {
                                const dispositionStr = response.headers.get("Content-Disposition");
                                const disposition = contentDisposition.parse(dispositionStr);
                                if (disposition.parameters.filename != null) {
                                    filename = disposition.parameters.filename;
                                }
                            }
                            handleApiSuccess(next, action, null, blob, filename);
                        });
                }
                else {
                    response.text()
                        .then((dataStr: string) => {
                            const data: ApiResponse = ApiHelper.deserialize(dataStr);
                            handleApiSuccess(next, action, data);
                        });
                }
            }
            else {
                if (isJson(response)) {
                    response.json()
                        .then((data: ApiResponse) => {
                            handleApiFailure(next, action, data.error);
                        });
                }
                else {
                    handleApiFailure(next, action, {
                        code: response.status,
                        message: response.statusText,
                    });
                }
            }
        })
        .catch((error: Response) => {
            // TODO Should handle status codes etc here
            if (isJson(error)) {
                error.json()
                    .then((data: ApiResponse) => {
                        handleApiFailure(next, action, data.error);
                    });
            }
            else {
                handleApiFailure(next, action, {
                    code: error.status,
                    message: error.statusText,
                });
            }
        });
}

function isJson(res: Response) {
    const contentType = res.headers.get("content-type");
    return contentType && contentType.indexOf("application/json") !== -1;
}

async function futch(url: string, opts: any, onProgress?: (e: ProgressEvent) => any) {
    return new Promise((resolve, reject) => {
        // Set up the request.
        const xhr = new XMLHttpRequest();
        xhr.open(opts.method || "get", url);
        if (opts.headers instanceof Headers) {
            (opts.headers as Headers).forEach((value: string, key: string) => {
                xhr.setRequestHeader(key, value);
            });
        }

        // Handle success, failure and progress.
        xhr.onload = () => resolve(responseFromXhr(xhr));
        xhr.onerror = () => reject(responseFromXhr(xhr));
        if (xhr.upload && onProgress) {
            xhr.upload.onprogress = onProgress;
        }

        // Send the request
        xhr.send(opts.body);
    });
}

function responseFromXhr(xhr: XMLHttpRequest) {
    // Build the headers object
    const headers = new Headers();
    const headersString = xhr.getAllResponseHeaders();
    if (headersString !== "") {
        const arr = headersString.trim().split(/[\r\n]+/);
        for (const line of arr) {
            const parts = line.split(": ");
            const header = parts.shift();
            const value = parts.join(": ");
            headers.append(header, value);
        }
    }

    // Return the response
    let status = xhr.status;
    if (status === 0) {
        status = 502;
    }
    return new Response(xhr.responseText, {
        status,
        statusText: xhr.statusText,
        headers,
    });
}

function handleApiRedirect(
    next: Dispatch<State>,
    action: Action<ApiRequest>,
    newEndpoint: string,
) {
    // Log the redirect.
    const request = action.payload;
    const { endpoint, types } = request.options;
    log.info("Redirecting from " + endpoint + " to " + newEndpoint);

    const id = request.getId();
    const newId = Number(newEndpoint.replace(endpoint.replace(String(id), ""), ""));

    const actionState: [number, number] = [id, newId];
    const creator = types[3];
    next(creator(actionState));

    // Send the expire action after the expiration time.
    setTimeout(() => {
        next(actions.apiRequestExpire(request));
    }, API_REQUEST_EXPIRATION);
}

function handleApiFailure(
    next: Dispatch<State>,
    action: Action<ApiRequest>,
    error: ApiResponseError = null,
    notLoggedIn: boolean = false,
) {
    // Log the errors.
    const request = action.payload;
    const apiOptions = request.options;
    let actionState: any = {};

    log.warn("Failed '" + apiOptions.method + "' API call to " + apiOptions.endpoint);
    if (notLoggedIn) {
        log.warn("Not logged in.");

        // Set the actionState.
        actionState = {
            type: "HTTP",
            message: "Not logged in.",
            request,
        } as HttpStateError;
    }
    else if (error != null) {
        log.warn(error.message);

        // Set the actionState.
        actionState = {
            type: "HTTP",
            message: error.message,
            request,
            code: error.code,
        } as HttpStateError;

        // Get in any sub-errors
        if (error.errors != null && error.errors.length > 0) {
            actionState.errors = [];
            for (const subError of error.errors) {
                // Assume all sub-errors are validation errors, but they might not be.
                const subStateError = {
                    type: "Validation",
                    message: subError.message,
                } as StateError;

                // Set the key
                if (subError.locationType != null &&
                    subError.locationType === "parameter" &&
                    subError.location != null
                ) {
                    subStateError.key = subError.location;
                }
                actionState.errors.push(subStateError);
            }
        }
    }

    // And dispatch the next actions.
    const responsePayload: ApiRequestErrorPair = {
        request,
        error: actionState,
    };
    next(actions.apiResponseFailure(responsePayload));
    if (apiOptions.types.length >= 3) {
        const creator = apiOptions.types[2];
        next(creator(actionState));
    }

    // Send the expire action after the expiration time.
    setTimeout(() => {
        next(actions.apiRequestExpire(request));
    }, API_REQUEST_EXPIRATION);
}

function handleApiSuccess(
    next: Dispatch<State>,
    action: Action<ApiRequest>,
    response: ApiResponse = null,
    blob: Blob = null,
    filename: string = null,
) {
    // Log the success.
    const request = action.payload;
    const { method, endpoint, types, body } = request.options;
    log.info("Successful '" + method + "' API call to " + endpoint);

    // And dispatch the next actions.
    let actionState: any = null;
    let successCreator: ActionCreator<any> = null;
    const hasResponse = types.length >= 2 && response !== null && response.data !== null;
    if (hasResponse) {
        const data = response.data;
        successCreator = types[1];

        // Get the data to pass to the reducer.
        if (isApiModel(data)) {
            // It's a single entity.
            // Normalize the data first.
            const normalized = ApiHelper.normaliseData(data.kind, data);
            actionState = ApiHelper.mapJsonToModel(data.kind, normalized);
        }
        else if (isApiModelCollection(data)) {
            // Normalize the data first.
            actionState = normaliseFromCollection(data.kind, data.items);

            // Handle if it's paginated.
            if (isApiModelCollectionPaginated(data)) {
                if (data.models != null) {
                    _.forOwn(data.models, (items, kind) => {
                        const norm = normaliseFromCollection(kind, items);
                        _.merge(actionState.entities, norm.entities);
                    });
                }

                const pageResult: PaginationResult = {
                    count: data.currentItemCount,
                    page: data.pageIndex,
                    perPage: data.itemsPerPage,
                    total: data.totalItems,
                    numPages: data.totalPages,
                    nextLink: data.nextLink,
                    previousLink: data.previousLink,
                    pagingLinkTemplate: data.pagingLinkTemplate,
                };
                if (data.totalItems != null) {
                    pageResult.total = data.totalItems;
                }
                if (data.totalPages != null) {
                    pageResult.numPages = data.totalPages;
                }
                if (isApiModelTrendsCollectionPaginated(data)) {
                    (pageResult as TrendsPaginationResult).from = request.query("from")
                        ? new Date(request.query("from")) : null;
                    (pageResult as TrendsPaginationResult).to = request.query("to")
                        ? new Date(request.query("to")) : null;
                    if (data.albumId != null) {
                        (pageResult as TrendsPaginationResult).albumId = data.albumId;
                    }
                    if (data.trackId != null) {
                        (pageResult as TrendsPaginationResult).trackId = data.trackId;
                    }
                    if (data.artistId != null) {
                        (pageResult as TrendsPaginationResult).artistId = data.artistId;
                    }
                }

                actionState = [pageResult, actionState];
            }
        }
        else if (isApiId(data)) {
            // Rebuild the model.
            actionState = ApiHelper.createModelFromRequest(endpoint, body, data.id);

            if (actionState === null) {
                const requestId = request.getId();
                actionState = data.id;
                if (requestId != null) {
                    actionState = [requestId, data.id];
                }
            }
        }
        else if (method === "PUT" && !( body instanceof Blob)) {
            // Rebuild the model
            const baseEndpoint = endpoint.substring(0, endpoint.indexOf("/"));
            actionState = ApiHelper.createModelFromRequest(baseEndpoint, body);
        }
        else if (method === "DELETE") {
            // Get the id of the object to delete
            actionState = Number(
                endpoint.substring(endpoint.indexOf("/") + 1),
            );
        }
        // Login is a special case
        else if (isApiResponseAuth(data)) {
            // Normalize the user.
            const normalised = ApiHelper.normaliseData("user", data.user);
            const { entities } = ApiHelper.mapJsonToModel("user", normalised);
            actionState = {
                accessToken: data.access_token,
                userEntities: entities,
            } as AuthDetails;
        }
        // As are sales
        else if (isApiSales(data)) {
            const from = request.query("from");
            const to = request.query("to");
            actionState = mapApiSales(from, to)(data);
        }
        // And demographics
        else if (isApiDemographics(data)) {
            const from = request.query("from");
            const to = request.query("to");
            actionState = mapApiDemographics(from, to)(data);
        }
        // Headline stats
        else if (isApiHeadlineStats(data)) {
            const from = request.query("from");
            const to = request.query("to");
            actionState = mapApiHeadlineStats(from, to)(data);
        }
        // And search
        else if (isApiSearch(data)) {
            // Get the IDs of the results in order.
            const albumIds = data.albums.map((item) => item.id);
            const trackIds = data.tracks.map((item) => item.id);
            // Then normalise
            const normalisedAlbums = ApiHelper.normaliseData("album", data.albums);
            const normalisedTracks = ApiHelper.normaliseData("track", data.tracks);
            const normalised = ApiHelper.mergeNormalisedEntities(normalisedAlbums, normalisedTracks);
            const { entities } = ApiHelper.mapJsonToModel("album", normalised);

            actionState = {
                query: request.query("q"),
                entities,
                albumIds,
                trackIds,
            };
        }
        else if (isApiUrl(data)) {
            actionState = data.url;
        }
        else if (method === "POST" || method === "PUT") {
            // Get the id of the object we're posting to
            actionState = request.getId();
        }
    }
    const responsePayload: ApiRequestResponse = {
        request,
        blob,
        filename,
        response: actionState,
    };
    next(actions.apiResponseSuccess(responsePayload));
    if (hasResponse) {
        next(successCreator(actionState));
    }

    // Send the expire action after the expiration time.
    setTimeout(() => {
        next(actions.apiRequestExpire(request));
    }, API_REQUEST_EXPIRATION);
}

const normaliseFromCollection = (kind: string, items: ApiModel[]) => {
    const normalised = ApiHelper.normaliseData(kind, items);
    let norm: any = [];
    if (normalised === items) {
        for (const item of items) {
            norm.push(ApiHelper.mapJsonToModel(kind, item));
        }
    }
    else {
        norm = ApiHelper.mapJsonToModel(kind, normalised);
    }
    return norm;
};

const mapApiSales = (from: string, to: string) => (sales: ApiSales) => {
    const mapped: any = {
        from: from != null ? new Date(from) : null,
        to: to != null ? new Date(to) : null,
        groupBy: sales.groupBy,
        data: sales.sales,
    };
    if (sales.albumId != null) {
        mapped.albumId = sales.albumId;
        if (sales.tracks != null) {
            mapped.tracks = _.map(sales.tracks, mapApiSales(from, to));
        }
    }
    else if (sales.trackId != null) {
        mapped.trackId = sales.trackId;
        if (sales.albums != null) {
            mapped.albums = _.map(sales.albums, mapApiSales(from, to));
        }
    }
    else if (sales.artistId != null) {
        mapped.artistId = sales.artistId;
        if (sales.albums != null) {
            mapped.albums = _.map(sales.albums, mapApiSales(from, to));
        }
    }
    else if (sales.countryCode != null) {
        mapped.countryCode = sales.countryCode;
    }
    if (sales.countries != null) {
        mapped.countries = _.map(sales.countries, mapApiSales(from, to));
    }
    return mapped;
};

const mapApiDemographics = (from: string, to: string) => (demographics: ApiDemographics) => {
    const mapped: any = {
        from: from != null ? new Date(from) : null,
        to: to != null ? new Date(to) : null,
        data: {
            age: demographics.age,
            gender: demographics.gender,
            region: demographics.region,
        },
        models: {
            regions: Map(demographics.models.regions.reduce((arr, region) => {
                arr.push([region.code, new Region(ApiHelper.objectFromApiModel(region))]);
                return arr;
            }, [])),
        },
    };
    if (demographics.album_id != null) {
        mapped.albumId = demographics.album_id;
    }
    else if (demographics.track_id != null) {
        mapped.trackId = demographics.track_id;
    }
    else if (demographics.artist_id != null) {
        mapped.artistId = demographics.artist_id;
    }
    return mapped;
};

const mapApiHeadlineStats = (from: string, to: string) =>
    ({ album_id, track_id, artist_id, headings, all, vendors, countries, countries_vendors }: ApiHeadlineStats) => {
        const mapped: any = {
            from: from != null ? new Date(from) : null,
            to: to != null ? new Date(to) : null,
            headings,
            data: { all, vendors, countries, countriesVendors: countries_vendors },
        };
        if (album_id != null) {
            mapped.albumId = album_id;
        }
        else if (track_id != null) {
            mapped.trackId = track_id;
        }
        else if (artist_id != null) {
            mapped.artistId = artist_id;
        }
        return mapped;
    };

export { api };
